import { ReactNode, createContext, useContext, useMemo } from 'react';
import { useQuery } from 'react-query';

import getConfirmedInvitations from 'api/matchmaking/getConfirmedInvitations';
import getInvitations from 'api/matchmaking/getInvitations';

import { IUseControllerContextProps } from './types';

const useControllerContext = createContext<IUseControllerContextProps>(
  {} as IUseControllerContextProps,
);

interface IUseControllerProviderProps {
  children: ReactNode;
}

const UseControllerAppointmentsProvider: React.FC<IUseControllerProviderProps> = ({ children }) => {
  const { data: confirmedInvitations = [], isLoading: isConfirmedMeetingsLoading } = useQuery(
    ['getConfirmedInvitations'],
    () => getConfirmedInvitations({}),
  );

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const { data: invitationsData = [], isLoading: isInvitationsLoading } = useQuery(
    ['getInvitations'],
    () => getInvitations(),
  );

  const havePendingInvitation = useMemo(
    () => invitationsData.some((invitation) => !invitation.owner) || false,
    [invitationsData],
  );

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const contextValue = useMemo(
    () => ({
      isLoading: isConfirmedMeetingsLoading || isInvitationsLoading,
      confirmedInvitations,
      havePendingInvitation,
    }),
    [confirmedInvitations, isConfirmedMeetingsLoading, havePendingInvitation, isInvitationsLoading],
  );

  return (
    <useControllerContext.Provider value={contextValue}>{children}</useControllerContext.Provider>
  );
};

const useControllerAppointments = (): IUseControllerContextProps => {
  const context = useContext(useControllerContext);
  if (!context) throw new Error('useController must be used within an UseControllerProvider');
  return context;
};

export { UseControllerAppointmentsProvider, useControllerAppointments };
